import React, { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { Autocomplete, Checkbox } from "@mui/material";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { toast } from "react-toastify";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { addProductsAndServices } from "slices/Masters/productsandservices/thunk";
import { fetchTypesBySubCategory } from "slices/Masters/type/thunk";
import { fetchCategoryByDropDown } from "slices/Masters/category/thunk";
import { fetchSuCategoryBasedOnSelectedCategory } from "slices/Masters/sub-category/thunk";
import { fetchSacCodeByDropDown } from "slices/Masters/sac-code/thunk";
import { fetchMakeByDropDown } from "slices/Masters/make/thunk";
import { fetchModelByMakeDropDown } from "slices/Masters/model/thunk";

interface ServiceFormValues {
  item_type: "services";
  item_name: string;
  category: string; // stores ID
  sub_category: string; // stores ID
  type: string[]; // stores array of type IDs
  make: string; // stores ID
  model: string; // stores ID
  service_code: string;
  unit_price: string;
  sac: string; // stores ID
  gst: string;
  status: boolean;
  description?: string;
  existing_image_ref?: string | null;
}

interface ServiceFormModalProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const ServiceFormModal: React.FC<ServiceFormModalProps> = (props) => {
  const { onSuccess, onCancel } = props;
  const dispatch = useDispatch<any>();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [image, setImage] = useState<File | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [sacCodes, setSacCodes] = useState<any[]>([]);
  const [makes, setMakes] = useState<any[]>([]);
  const [models, setModels] = useState<any[]>([]);
  const prevCategoryRef = useRef<string>("");
  const prevMakeRef = useRef<string>("");

  const initialValues: ServiceFormValues = {
    item_type: "services",
    item_name: "",
    category: "",
    sub_category: "",
    type: [],
    make: "",
    model: "",
    service_code: "",
    unit_price: "",
    sac: "",
    gst: "",
    status: true,
    description: "",
    existing_image_ref: "",
  };

  const validationSchema = Yup.object().shape({
    item_name: Yup.string().required("Service Name is required"),
    category: Yup.string().required("Category is required"),
    sub_category: Yup.string().required("Sub-Category is required"),
    service_code: Yup.string().required("Service Code is required"),
    unit_price: Yup.number()
      .typeError("Unit Price must be a number")
      .positive("Unit Price must be greater than 0")
      .required("Unit Price is required"),
    sac: Yup.string().required("SAC Code is required"),
    gst: Yup.string()
      .required("GST % is required")
      .test("is-valid-gst", "GST % must be a valid number", (value) => {
        if (!value) return false;
        const num = parseFloat(value);
        return !isNaN(num) && num >= 0 && num <= 100;
      }),
  });

  const formik = useFormik<ServiceFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const formData = new FormData();

        formData.append("item_type", values.item_type);
        formData.append("item_name", values.item_name);
        formData.append("service_code", values.service_code);
        if (values.unit_price) formData.append("unit_price", values.unit_price);

        if (values.category) formData.append("category", values.category);
        if (values.sub_category)
          formData.append("sub_category", values.sub_category);
        if (values.type && values.type.length > 0) {
          values.type.forEach((typeId: string) => {
            formData.append("type", typeId);
          });
        }
        if (values.make) formData.append("make", values.make);
        if (values.model) formData.append("model", values.model);
        if (values.sac) formData.append("sac", values.sac);

        if (values.gst) formData.append("gst_percent", values.gst);
        formData.append("status", values.status ? "1" : "0");
        if (values.description)
          formData.append("description", values.description);

        if (image) {
          formData.append("attachment", image);
        }

        await dispatch(addProductsAndServices(formData)).unwrap();
        toast.success("Service added successfully");

        // If onSuccess callback is provided, call it instead of navigating
        if (onSuccess) {
          onSuccess();
        }
      } catch (err: any) {
        const duplicateMessage = err?.errors?.item_name?.[0];
        if (duplicateMessage) {
          toast.error("Service with this name already exists");
        } else {
          toast.error(err?.message || "Failed to save");
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Fetch dropdowns
  useEffect(() => {
    const loadDropdowns = async () => {
      const [cat, sac, make] = await Promise.all([
        dispatch(fetchCategoryByDropDown()).unwrap(),
        dispatch(fetchSacCodeByDropDown()).unwrap(),
        dispatch(fetchMakeByDropDown()).unwrap(),
      ]);
      setCategories(cat?.data || []);
      setSacCodes(sac?.data || []);
      setMakes(make?.data || []);
    };
    loadDropdowns();
  }, [dispatch]);

  // Fetch subcategories
  useEffect(() => {
    const currentCategory = formik.values.category;
    // Reset sub_category when category changes
    if (
      prevCategoryRef.current &&
      prevCategoryRef.current !== currentCategory
    ) {
      formik.setFieldValue("sub_category", "");
    }
    prevCategoryRef.current = currentCategory;

    if (currentCategory) {
      dispatch(fetchSuCategoryBasedOnSelectedCategory(currentCategory))
        .unwrap()
        .then((res: any) => setSubCategories(res?.data || []))
        .catch(() => setSubCategories([]));
    } else {
      setSubCategories([]);
    }
  }, [formik.values.category, dispatch]);

  // Fetch models
  useEffect(() => {
    const currentMake = formik.values.make;
    // Reset model when make changes
    if (prevMakeRef.current && prevMakeRef.current !== currentMake) {
      formik.setFieldValue("model", "");
    }
    prevMakeRef.current = currentMake;

    if (currentMake) {
      dispatch(fetchModelByMakeDropDown(currentMake))
        .unwrap()
        .then((res: any) => setModels(res || []))
        .catch(() => setModels([]));
    } else {
      setModels([]);
    }
  }, [formik.values.make, dispatch]);

  // Fetch types when sub-category changes
  useEffect(() => {
    if (formik.values.sub_category) {
      dispatch(fetchTypesBySubCategory(formik.values.sub_category))
        .unwrap()
        .then((res: any) => setTypes(res?.data || []))
        .catch(() => setTypes([]));
    } else {
      setTypes([]);
      formik.setFieldValue("type", []);
    }
  }, [formik.values.sub_category, dispatch]);

  const commonInputStyle = {
    fontSize: "0.8rem",
    minHeight: 38,
    padding: "0.375rem 0.75rem",
  };
  const commonLabelStyle = {
    fontSize: "0.8rem",
    marginBottom: 6,
    marginTop: 12,
  };

  return (
    <Form autoComplete="off" onSubmit={formik.handleSubmit} noValidate>
      {/* Basic Details */}
      <Row className="mb-3">
        <Col xs={12} md={4}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Service Name <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            name="item_name"
            value={formik.values.item_name}
            onChange={formik.handleChange}
            placeholder="Enter Service Name"
            isInvalid={!!formik.errors.item_name && formik.touched.item_name}
            onBlur={formik.handleBlur}
            style={commonInputStyle}
          />
          <Form.Control.Feedback type="invalid">
            {formik.errors.item_name}
          </Form.Control.Feedback>
        </Col>
        <Col xs={12} md={4}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Category <span className="text-danger">*</span>
          </Form.Label>
          <Autocomplete
            options={categories}
            getOptionLabel={(option) => option.category_name || ""}
            isOptionEqualToValue={(option, value) =>
              option.category_id === value.category_id
            }
            value={
              categories.find(
                (c) => c.category_id === formik.values.category
              ) || null
            }
            onChange={(_, newValue) => {
              formik.setFieldValue("category", newValue?.category_id || "");
              formik.setFieldValue("sub_category", ""); // Reset sub_category when category changes
            }}
            onBlur={() => {
              formik.setFieldTouched("category", true);
            }}
            renderInput={(params) => (
              <BootstrapTextField
                {...params}
                placeholder="Select Category"
                size="small"
                error={!!formik.errors.category && formik.touched.category}
                helperText={
                  formik.errors.category && formik.touched.category
                    ? formik.errors.category
                    : ""
                }
              />
            )}
          />
        </Col>
        <Col xs={12} md={4}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Sub-Category <span className="text-danger">*</span>
          </Form.Label>
          <Autocomplete
            options={subCategories}
            getOptionLabel={(option) => option.sub_category_name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={
              subCategories.find(
                (sc) => sc.id === formik.values.sub_category
              ) || null
            }
            onChange={(_, newValue) => {
              formik.setFieldValue("sub_category", newValue?.id || "");
              formik.setFieldValue("type", []); // Reset type when sub_category changes
            }}
            onBlur={() => {
              formik.setFieldTouched("sub_category", true);
            }}
            disabled={!formik.values.category}
            renderInput={(params) => (
              <BootstrapTextField
                {...params}
                placeholder="Select Sub-Category"
                size="small"
                error={
                  !!formik.errors.sub_category && formik.touched.sub_category
                }
                helperText={
                  formik.errors.sub_category && formik.touched.sub_category
                    ? formik.errors.sub_category
                    : ""
                }
              />
            )}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={12} md={4}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Type
          </Form.Label>
          <Autocomplete
            multiple
            options={types}
            value={types.filter((type) => formik.values.type.includes(type.id))}
            onChange={(event, selectedTypes) => {
              const selectedIds = selectedTypes.map((type) => type.id);
              formik.setFieldValue("type", selectedIds);
            }}
            getOptionLabel={(option) => option.type_name || ""}
            disableCloseOnSelect
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} style={{ marginRight: 8 }} />
                {option.type_name}
              </li>
            )}
            renderInput={(params) => (
              <BootstrapTextField
                {...params}
                placeholder="Select Types"
                size="small"
              />
            )}
            disabled={!formik.values.sub_category}
          />
        </Col>
        <Col xs={12} md={4}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Make
          </Form.Label>
          <Autocomplete
            options={makes}
            getOptionLabel={(option) => option.make_name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={makes.find((m) => m.id === formik.values.make) || null}
            onChange={(_, newValue) => {
              formik.setFieldValue("make", newValue?.id || "");
              formik.setFieldValue("model", ""); // Reset model when make changes
            }}
            onBlur={() => {
              formik.setFieldTouched("make", true);
            }}
            renderInput={(params) => (
              <BootstrapTextField
                {...params}
                placeholder="Select Make"
                size="small"
              />
            )}
          />
        </Col>
        <Col xs={12} md={4}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Model
          </Form.Label>
          <Autocomplete
            options={models}
            getOptionLabel={(option) => option.model_name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={models.find((mo) => mo.id === formik.values.model) || null}
            onChange={(_, newValue) => {
              formik.setFieldValue("model", newValue?.id || "");
            }}
            onBlur={() => {
              formik.setFieldTouched("model", true);
            }}
            disabled={!formik.values.make}
            renderInput={(params) => (
              <BootstrapTextField
                {...params}
                placeholder="Select Model"
                size="small"
              />
            )}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={12} md={4}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Service Code <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            name="service_code"
            value={formik.values.service_code}
            onChange={formik.handleChange}
            placeholder="Enter Service Code"
            isInvalid={
              !!formik.errors.service_code && formik.touched.service_code
            }
            onBlur={formik.handleBlur}
            style={commonInputStyle}
          />
          <Form.Control.Feedback type="invalid">
            {formik.errors.service_code}
          </Form.Control.Feedback>
        </Col>
        <Col xs={12} md={4}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Unit Price <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            name="unit_price"
            value={formik.values.unit_price}
            onChange={formik.handleChange}
            placeholder="Enter Unit Price"
            onBlur={formik.handleBlur}
            isInvalid={!!formik.errors.unit_price && formik.touched.unit_price}
            style={commonInputStyle}
          />
          <Form.Control.Feedback type="invalid">
            {formik.errors.unit_price}
          </Form.Control.Feedback>
        </Col>
      </Row>

      {/* Additional Information Section */}
      <div className="mt-4">
        <h5 className="mb-3">Additional Information</h5>
        <Row className="mb-3">
          <Col xs={12} md={6}>
            <Form.Label className="fw-semibold" style={commonLabelStyle}>
              SAC Code <span className="text-danger">*</span>
            </Form.Label>
            <Autocomplete
              options={sacCodes}
              getOptionLabel={(option) => option.sac_code || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={sacCodes.find((s) => s.id === formik.values.sac) || null}
              onChange={(_, newValue) => {
                formik.setFieldValue("sac", newValue?.id || "");
                // Auto-fill GST from SAC code
                if (newValue?.gst_percentage) {
                  formik.setFieldValue(
                    "gst",
                    newValue.gst_percentage.toString()
                  );
                } else {
                  formik.setFieldValue("gst", "");
                }
              }}
              onBlur={() => {
                formik.setFieldTouched("sac", true);
              }}
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  placeholder="Select SAC Code"
                  size="small"
                  error={!!formik.errors.sac && formik.touched.sac}
                  helperText={
                    formik.errors.sac && formik.touched.sac
                      ? formik.errors.sac
                      : ""
                  }
                />
              )}
            />
          </Col>
          <Col xs={12} md={6}>
            <Form.Label className="fw-semibold" style={commonLabelStyle}>
              GST % <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              name="gst"
              value={formik.values.gst}
              placeholder="GST % (Auto-filled from SAC)"
              readOnly
              isInvalid={!!formik.errors.gst && formik.touched.gst}
              style={commonInputStyle}
            />
            {formik.errors.gst && formik.touched.gst && (
              <Form.Control.Feedback
                type="invalid"
                style={{ display: "block" }}
              >
                {formik.errors.gst}
              </Form.Control.Feedback>
            )}
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Upload Image
              </Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const target = e.target as HTMLInputElement;
                  const file = target.files?.[0] || null;
                  setImage(file);
                  if (file) setExistingImageUrl(null);
                }}
                style={commonInputStyle}
              />

              {/* Preview new image if selected */}
              {image && (
                <div className="mt-2">
                  <img
                    src={URL.createObjectURL(image)}
                    alt="preview"
                    className="img-thumbnail"
                    style={{ maxHeight: "120px" }}
                  />
                </div>
              )}

              {/* Show existing image if editing and no new image selected */}
              {!image && existingImageUrl && (
                <div className="mt-2">
                  <img
                    src={existingImageUrl}
                    alt="existing"
                    className="img-thumbnail"
                    style={{ maxHeight: "120px" }}
                  />
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* Buttons */}
      <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
        <CancelButton
          onClick={() => {
            if (onCancel) {
              onCancel();
            }
          }}
          disabled={isSubmitting}
          showConfirmation={false}
        />
        <PrimarySubmitButton
          label="Add"
          isLoading={isSubmitting}
          loadingLabel="Adding..."
        />
      </div>
    </Form>
  );
};

export default ServiceFormModal;
